import React from 'react'
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { theme } from './theme/theme';
import { BrowserRouter, Route, Routes, Navigate } from 'react-router-dom'
import Layout from './components/common/Layout'
import Dashboard from './components/dashboard/Dashboard'
import SignIn from './components/auth/SignIn'
import { PrivateRoute } from './components/auth/PrivateRoute';
import { ProtectedRoute } from './components/auth/ProtectedRoute';
import { UnauthorizedAccess } from './components/common/UnauthorizedAccess';
import WorkItems from "./components/pages/applications/WorkItems";
import WorkItemTracker from "./components/pages/applications/WorkItemTracker";
import WorkItemView from "./components/pages/applications/WorkItemView";
import Leads from "./components/pages/applications/Leads";
import LeadDetails from "./components/pages/applications/LeadDetails";
import LoanRequests from "./components/pages/applications/LoanRequests";
import LoanApplicationWizard from './components/loan-application/LoanApplicationWizard';
import PendingSubmissions from './components/pages/applications/PendingSubmissions';
import Applications from './components/pages/applications/Applications';
import ApplicationDetails from './components/pages/applications/ApplicationDetails';
import DocumentManagement from './components/pages/applications/DocumentManagement';
import DocumentUpload from './components/pages/applications/DocumentUpload';
import Reports from './components/pages/reports/Reports';
import DeclineReasonsReport from './components/pages/reports/DeclineReasonsReport';
import LoanApplicationDocumentsRoute from './components/pages/applications/LoanApplicationDocumentsRoute';
import IndividualPricingSettings from './components/pages/admin/IndividualPricingSettings';
import DocumentRequirementsSettings from './components/pages/admin/DocumentRequirementsSettings';
import LoanCustomers from './components/pages/customers/LoanCustomers';
import Birthdays from './components/pages/customers/Birthdays';
import IndividualCustomers from './components/pages/customers/IndividualCustomers';
import IndividualCustomerForm from './components/pages/customers/IndividualCustomerForm';
import IndividualCustomerDetails from './components/pages/customers/IndividualCustomerDetails';
import IndividualCustomerDocuments from './components/pages/customers/IndividualCustomerDocuments';
import CustomerWizard from './components/pages/customers/CustomerWizard';
import CustomerDetails from './components/pages/customers/CustomerDetails';
import LoanApplicationDocumentView from './components/pages/applications/LoanApplicationDocumentView';

import LoanAccounts from './components/pages/accounts/LoanAccounts';
import CreateDirectLoanAccount from './components/pages/accounts/CreateDirectLoanAccount';
import CustomerAccounts from './components/pages/accounts/CustomerAccounts';
import CustomerDocuments from './components/pages/customers/CustomerDocuments';
import CustomerOpeningBalance from './components/pages/customers/CustomerOpeningBalance';

// Loan Processing Pages
import Verification from './components/pages/loan-processing/Verification';
import VerificationDetails from './components/pages/loan-processing/VerificationDetails';
import CreditAssessment from './components/pages/loan-processing/CreditAssessment';
import CreditAssessmentDetails from './components/pages/loan-processing/CreditAssessmentDetails';
import CreditApprovalDetails from './components/pages/loan-processing/CreditApprovalDetails';
import LoanAgreement from './components/pages/loan-processing/LoanAgreement';
import LoanAgreementDetails from './components/pages/loan-processing/LoanAgreementDetails';
import Disbursements from './components/pages/loan-processing/Disbursements';
import DisbursementDetails from './components/pages/loan-processing/DisbursementDetails';
import CollateralVerification from './components/pages/loan-processing/CollateralVerification';
import CollateralVerificationDetails from './components/pages/loan-processing/CollateralVerificationDetails';

// Collections Pages
import DueCollections from './components/pages/collections/DueCollections';
import OverdueCollections from './components/pages/collections/OverdueCollections';
import SplitPayment from './components/pages/collections/SplitPayment';
import LoanAccountDetails from './components/pages/collections/LoanAccountDetails';
import PendingPenalities from './components/pages/collections/PendingPenalties';
import LoanAccountPenalties from './components/pages/collections/LoanAccountPenalties';
import PaymentSummary from './components/pages/collections/PaymentSummary';
import PaymentOverview from './components/pages/collections/PaymentOverview';

// Loan Book Pages
import LoanBookSummary from './components/pages/loanbook/LoanBookSummary';
import LoanBookRecordView from './components/pages/loanbook/LoanBookRecordView';
import LoanInvoices from './components/pages/loanbook/LoanInvoices';
import CollectionsOverview from './components/pages/loanbook/CollectionsOverview';

// Admin Pages
import Users from './components/pages/admin/Users';
import Documents from './components/pages/applications/Documents';
import DocumentArchive from './components/pages/documents/DocumentArchive';
import ArchiveBusinessFile from './components/pages/documents/ArchiveBusinessFile';
import ArchiveCustomerDocuments from './components/pages/documents/ArchiveCustomerDocuments';
import ArchiveLoanFolders from './components/pages/documents/ArchiveLoanFolders';
import ArchiveLoanFolder from './components/pages/documents/ArchiveLoanFolder';
import Settings from './components/pages/admin/Settings';
import Notifications from './components/pages/notifications/Notifications';
import Communication from './components/pages/communication/Communication';
import CreditAssessmentStandalone from './components/pages/credit/CreditAssessmentStandalone';
import CreditReports from './components/pages/credit/CreditReports';
import CreditReportHistory from './components/pages/credit/CreditReportHistory';
import MyProfile from './components/pages/profile/MyProfile';
import { RoutePermissionManager } from './components/admin/RoutePermissionManager';

import ResolutionWrapper from './components/common/ResolutionWrapper';

const App = () => {
    return (
        <ResolutionWrapper
            enableScaling={true}
            enableFontScaling={true}
            enableSpacingAdjustments={false}
        >
            <ThemeProvider theme={theme}>
                <CssBaseline />
                <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
                    <Routes>
                        <Route path="/auth-signin" element={<SignIn />} />
                        <Route path="/" element={
                            <PrivateRoute>
                                <Layout />
                            </PrivateRoute>
                        }>
                            <Route index element={
                                <ProtectedRoute>
                                    <Dashboard />
                                </ProtectedRoute>
                            } />
                            <Route path="/dashboard" element={
                                <ProtectedRoute>
                                    <Dashboard />
                                </ProtectedRoute>
                            } />

                            {/* Application Routes */}
                            <Route path="/work-items" element={
                                <ProtectedRoute>
                                    <WorkItems />
                                </ProtectedRoute>
                            } />
                            <Route path="/work-item-tracker" element={
                                <ProtectedRoute>
                                    <WorkItemTracker />
                                </ProtectedRoute>
                            } />
                            <Route path="/work-items/:taskId" element={
                                <ProtectedRoute>
                                    <WorkItemView />
                                </ProtectedRoute>
                            } />
                            <Route path="/leads" element={
                                <ProtectedRoute>
                                    <Leads />
                                </ProtectedRoute>
                            } />
                            <Route path="/loan-requests" element={
                                <ProtectedRoute>
                                    <LoanRequests />
                                </ProtectedRoute>
                            } />
                            <Route path="/leads/:id" element={
                                <ProtectedRoute>
                                    <LeadDetails />
                                </ProtectedRoute>
                            } />
                            <Route path="/loan-application/new" element={
                                <ProtectedRoute>
                                    <LoanApplicationWizard />
                                </ProtectedRoute>
                            } />
                            <Route path="/pending-submission" element={
                                <ProtectedRoute>
                                    <PendingSubmissions />
                                </ProtectedRoute>
                            } />
                            <Route path="/applications" element={
                                <ProtectedRoute>
                                    <Applications />
                                </ProtectedRoute>
                            } />
                            <Route path="/application/:id" element={
                                <ProtectedRoute>
                                    <ApplicationDetails />
                                </ProtectedRoute>
                            } />
                            <Route path="/loan-application/edit/:id" element={
                                <ProtectedRoute>
                                    <LoanApplicationWizard />
                                </ProtectedRoute>
                            } />
                            <Route path="/document-management/:taskId" element={
                                <ProtectedRoute>
                                    <DocumentManagement />
                                </ProtectedRoute>
                            } />
                            <Route path="/document-upload/:taskId" element={
                                <ProtectedRoute>
                                    <DocumentUpload />
                                </ProtectedRoute>
                            } />
                            <Route path="/loan-application-documents/:id" element={
                                <ProtectedRoute>
                                    <LoanApplicationDocumentsRoute />
                                </ProtectedRoute>
                            } />
                            <Route path="/loan-application-documents/view/:id" element={
                                <ProtectedRoute>
                                    <LoanApplicationDocumentView />
                                </ProtectedRoute>
                            } />

                            {/* Loan Processing Routes */}
                            <Route path="/verification" element={
                                <ProtectedRoute>
                                    <Verification />
                                </ProtectedRoute>
                            } />
                            <Route path="/verification/:applicationId" element={
                                <ProtectedRoute>
                                    <VerificationDetails />
                                </ProtectedRoute>
                            } />
                            <Route path="/credit-assessment" element={
                                <ProtectedRoute>
                                    <CreditAssessment />
                                </ProtectedRoute>
                            } />
                            <Route path="/credit-assessment/:applicationId" element={
                                <ProtectedRoute>
                                    <CreditAssessmentDetails />
                                </ProtectedRoute>
                            } />
                            <Route path="/credit-approval/:taskId" element={
                                <ProtectedRoute>
                                    <CreditApprovalDetails />
                                </ProtectedRoute>
                            } />
                            <Route path="/collateral-verification" element={
                                <ProtectedRoute>
                                    <CollateralVerification />
                                </ProtectedRoute>
                            } />
                            <Route path="/collateral-verification/:taskId" element={
                                <ProtectedRoute>
                                    <CollateralVerificationDetails />
                                </ProtectedRoute>
                            } />
                            <Route path="/loan-agreement" element={
                                <ProtectedRoute>
                                    <LoanAgreement />
                                </ProtectedRoute>
                            } />
                            <Route path="/loan-agreement/:taskId" element={
                                <ProtectedRoute>
                                    <LoanAgreementDetails />
                                </ProtectedRoute>
                            } />
                            <Route path="/disbursements" element={
                                <ProtectedRoute>
                                    <Disbursements />
                                </ProtectedRoute>
                            } />
                            <Route path="/disbursements/:taskId" element={
                                <ProtectedRoute>
                                    <DisbursementDetails />
                                </ProtectedRoute>
                            } />

                            {/* Collections Routes */}
                            <Route path="/collections/due" element={
                                <ProtectedRoute>
                                    <DueCollections />
                                </ProtectedRoute>
                            } />
                            <Route path="/collections/overdue" element={
                                <ProtectedRoute>
                                    <OverdueCollections />
                                </ProtectedRoute>
                            } />
                            {/* The old All Collections and Record Payment pages showed sample data and only pretended to
                                record payments; their addresses now open the real collections screens. */}
                            <Route path="/collections" element={<Navigate to="/loan-book/collections-overview" replace />} />
                            <Route path="/collections/payment/:id" element={<Navigate to="/collections/due" replace />} />
                            {/* One receipt settling more than one loan - see SplitPayment.tsx */}
                            <Route path="/collections/split-payment" element={
                                <ProtectedRoute>
                                    <SplitPayment />
                                </ProtectedRoute>
                            } />
                            <Route path="/loan-accounts/:id" element={
                                <ProtectedRoute>
                                    <LoanAccountDetails />
                                </ProtectedRoute>
                            } />
                            <Route path="/collections/penalties" element={
                                <ProtectedRoute>
                                    <PendingPenalities />
                                </ProtectedRoute>
                            } />
                            <Route path="/collections/penalties/:loanAccountId" element={
                                <ProtectedRoute>
                                    <LoanAccountPenalties />
                                </ProtectedRoute>
                            } />
                            <Route path="/collections/payment-summary" element={
                                <ProtectedRoute>
                                    <PaymentSummary />
                                </ProtectedRoute>
                            } />
                            <Route path="/collections/payment-overview" element={
                                <ProtectedRoute>
                                    <PaymentOverview />
                                </ProtectedRoute>
                            } />

                            {/* Credit Routes */}
                            <Route path="/credit-assessment-standalone" element={
                                <ProtectedRoute>
                                    <CreditAssessmentStandalone />
                                </ProtectedRoute>
                            } />
                            <Route path="/credit-reports" element={
                                <ProtectedRoute>
                                    <CreditReports />
                                </ProtectedRoute>
                            } />
                            <Route path="/credit-report-history" element={
                                <ProtectedRoute>
                                    <CreditReportHistory />
                                </ProtectedRoute>
                            } />

                            {/* Loan Book Routes */}
                            <Route path="/loan-book/summary" element={
                                <ProtectedRoute>
                                    <LoanBookSummary />
                                </ProtectedRoute>
                            } />
                            <Route path="/loan-book/overview" element={
                                <Navigate to="/loan-book/invoices" replace />
                            } />
                            <Route path="/loan-book/record/:id" element={
                                <ProtectedRoute>
                                    <LoanBookRecordView />
                                </ProtectedRoute>
                            } />
                            <Route path="/loan-book/invoices" element={
                                <ProtectedRoute>
                                    <LoanInvoices />
                                </ProtectedRoute>
                            } />
                            <Route path="/loan-book/collections-overview" element={
                                <ProtectedRoute>
                                    <CollectionsOverview />
                                </ProtectedRoute>
                            } />

                            {/* Reports Route */}
                            <Route path="/reports" element={
                                <ProtectedRoute>
                                    <Reports />
                                </ProtectedRoute>
                            } />
                            <Route path="/reports/decline-reasons" element={
                                <ProtectedRoute>
                                    <DeclineReasonsReport />
                                </ProtectedRoute>
                            } />

                            {/* Profile Route */}
                            <Route path="/my-profile" element={
                                <ProtectedRoute>
                                    <MyProfile />
                                </ProtectedRoute>
                            } />

                            {/* Admin Routes */}
                            <Route path="/users" element={
                                <ProtectedRoute>
                                    <Users />
                                </ProtectedRoute>
                            } />
                            {/* Document management: the loan document archive (business document spec) */}
                            <Route path="/documents" element={
                                <ProtectedRoute>
                                    <DocumentArchive />
                                </ProtectedRoute>
                            } />
                            <Route path="/documents/files/:customerId" element={
                                <ProtectedRoute>
                                    <ArchiveBusinessFile />
                                </ProtectedRoute>
                            } />
                            <Route path="/documents/files/:customerId/customer" element={
                                <ProtectedRoute>
                                    <ArchiveCustomerDocuments />
                                </ProtectedRoute>
                            } />
                            <Route path="/documents/files/:customerId/loans" element={
                                <ProtectedRoute>
                                    <ArchiveLoanFolders />
                                </ProtectedRoute>
                            } />
                            <Route path="/documents/loans/:applicationId" element={
                                <ProtectedRoute>
                                    <ArchiveLoanFolder />
                                </ProtectedRoute>
                            } />
                            {/* Applications still collecting their documents (the former Documents page) */}
                            <Route path="/documents/in-progress" element={
                                <ProtectedRoute>
                                    <Documents />
                                </ProtectedRoute>
                            } />
                            <Route path="/settings" element={
                                <ProtectedRoute>
                                    <Settings />
                                </ProtectedRoute>
                            } />
                            <Route path="/settings/individual-pricing" element={
                                <ProtectedRoute><IndividualPricingSettings /></ProtectedRoute>
                            } />
                            <Route path="/settings/document-requirements" element={
                                <ProtectedRoute><DocumentRequirementsSettings /></ProtectedRoute>
                            } />
                            {/* Section-specific settings, so the sidebar can link to one
                                directly and the section survives a refresh or a shared link. */}
                            <Route path="/settings/:section" element={
                                <ProtectedRoute>
                                    <Settings />
                                </ProtectedRoute>
                            } />
                            <Route path="/notifications" element={
                                <ProtectedRoute>
                                    <Notifications />
                                </ProtectedRoute>
                            } />
                            <Route path="/communication" element={
                                <ProtectedRoute>
                                    <Communication />
                                </ProtectedRoute>
                            } />
                            <Route path="/admin/route-permissions" element={
                                <ProtectedRoute>
                                    <RoutePermissionManager />
                                </ProtectedRoute>
                            } />

                            {/* Customer Management Routes */}
                            <Route path="/birthdays" element={
                                <ProtectedRoute>
                                    <Birthdays />
                                </ProtectedRoute>
                            } />
                            <Route path="/loan-customers" element={
                                <ProtectedRoute>
                                    <LoanCustomers />
                                </ProtectedRoute>
                            } />
                            <Route path="/loan-customers/new" element={
                                <ProtectedRoute>
                                    <CustomerWizard />
                                </ProtectedRoute>
                            } />
                                                        <Route path="/loan-customers/:id/edit" element={
                                <ProtectedRoute>
                                    <CustomerWizard />
                                </ProtectedRoute>
                            } />
                            <Route path="/loan-customers/:id/view" element={
                                <ProtectedRoute>
                                    <CustomerDetails />
                                </ProtectedRoute>
                            } />

                            <Route path="/loan-customers/:id/view" element={
                                <ProtectedRoute>
                                    <CustomerDetails />
                                </ProtectedRoute>
                            } />
                            <Route path="/loan-customers/:id/documents" element={
                                <ProtectedRoute>
                                    <CustomerDocuments />
                                </ProtectedRoute>
                            } />
                            <Route path="/loan-customers/:customerId/opening-balance" element={
                                <ProtectedRoute>
                                    <CustomerOpeningBalance />
                                </ProtectedRoute>
                            } />

                            <Route path="/individual-customers" element={<ProtectedRoute><IndividualCustomers /></ProtectedRoute>} />
                            <Route path="/individual-customers/new" element={<ProtectedRoute><IndividualCustomerForm /></ProtectedRoute>} />
                            <Route path="/individual-customers/:id/edit" element={<ProtectedRoute><IndividualCustomerForm /></ProtectedRoute>} />
                            <Route path="/individual-customers/:id/view" element={<ProtectedRoute><IndividualCustomerDetails /></ProtectedRoute>} />
                            <Route path="/individual-customers/:id/documents" element={<ProtectedRoute><IndividualCustomerDocuments /></ProtectedRoute>} />
                            <Route path="/individual-customers/:customerId/opening-balance" element={<ProtectedRoute><CustomerOpeningBalance customerBasePath="/individual-customers" customerListLabel="Individual Customers" /></ProtectedRoute>} />

                            {/* Account Management Routes */}
                            <Route path="/accounts" element={
                                <ProtectedRoute>
                                    <LoanAccounts />
                                </ProtectedRoute>
                            } />
                            <Route path="/accounts/create-direct" element={
                                <ProtectedRoute>
                                    <CreateDirectLoanAccount />
                                </ProtectedRoute>
                            } />
                            <Route path="/customers/:customerNumber/accounts" element={
                                <ProtectedRoute>
                                    <CustomerAccounts />
                                </ProtectedRoute>
                            } />

                            {/* Unauthorized Access Route */}
                            <Route path="/unauthorized" element={<UnauthorizedAccess />} />
                        </Route>
                    </Routes>
                </BrowserRouter>
            </ThemeProvider>
        </ResolutionWrapper>
    )
};

export default App
